import m from 'mithril'
import type { ListVisibility } from '../shared/types.ts'

const STAR_PATH =
  'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'

const star = (size: number, fill: 'full' | 'half' | 'empty') =>
  m('span.star', { class: fill, style: { width: `${size}px`, height: `${size}px` } }, [
    m('svg.star-base', { viewBox: '0 0 24 24', width: size, height: size, 'aria-hidden': 'true' }, m('path', { d: STAR_PATH })),
    m('svg.star-fill', { viewBox: '0 0 24 24', width: size, height: size, 'aria-hidden': 'true' }, m('path', { d: STAR_PATH })),
  ])

const starsFor = (value: number, size: number) =>
  [1, 2, 3, 4, 5].map((index) => star(size, value >= index ? 'full' : value >= index - 0.5 ? 'half' : 'empty'))

/** Read-only stars, rounded to the nearest half. */
export const Stars: m.Component<{ value: number; size?: number }> = {
  view: ({ attrs }) =>
    m(
      'span.stars',
      { role: 'img', 'aria-label': `${attrs.value} out of 5` },
      starsFor(Math.round(attrs.value * 2) / 2, attrs.size ?? 15),
    ),
}

/**
 * Half-star input: the left half of a star sets x.5, the right half the whole star, and dragging
 * across sets it as you slide. Tapping your current rating clears it. Arrow keys step by halves.
 */
export const RatingPicker: m.ClosureComponent<{ value: number; onchange: (value: number) => void }> = () => {
  let dragging = false
  let moved = false
  let startValue = 0

  const valueAt = (event: PointerEvent) => {
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const fraction = (event.clientX - box.left) / box.width
    return Math.min(5, Math.max(0.5, Math.ceil(fraction * 10) / 2))
  }

  return {
    view: ({ attrs }) =>
      m('div.rating-row', [
        m(
          'div.rating-picker',
          {
            role: 'slider',
            tabindex: 0,
            'aria-label': 'Rating',
            'aria-valuemin': 0,
            'aria-valuemax': 5,
            'aria-valuenow': attrs.value,
            'aria-valuetext': attrs.value ? `${attrs.value} stars` : 'Not rated',
            onpointerdown: (event: PointerEvent) => {
              ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
              dragging = true
              moved = false
              startValue = attrs.value
              const value = valueAt(event)
              if (value !== attrs.value) {
                moved = true
                attrs.onchange(value)
              }
            },
            onpointermove: (event: PointerEvent) => {
              if (!dragging) return
              const value = valueAt(event)
              if (value !== attrs.value) {
                moved = true
                attrs.onchange(value)
              }
            },
            onpointerup: () => {
              if (dragging && !moved && startValue === attrs.value) attrs.onchange(0)
              dragging = false
            },
            onpointercancel: () => {
              dragging = false
            },
            onkeydown: (event: KeyboardEvent) => {
              const step = { ArrowRight: 0.5, ArrowUp: 0.5, ArrowLeft: -0.5, ArrowDown: -0.5 }[event.key]
              if (step == null) return
              event.preventDefault()
              attrs.onchange(Math.min(5, Math.max(0, attrs.value + step)))
            },
          },
          starsFor(attrs.value, 34),
        ),
        m('span.rating-value', attrs.value ? attrs.value.toFixed(1) : 'Tap to rate'),
      ]),
  }
}

export const Spinner: m.Component = {
  view: () => m('div.spinner', m('span'), m('span'), m('span')),
}

export const Empty: m.Component<{ text: string }> = {
  view: ({ attrs }) => m('p.empty', attrs.text),
}

export const ErrorNote: m.Component<{ text: string }> = {
  view: ({ attrs }) => m('p.error-note', attrs.text),
}

export const formatDate = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export const timeAgo = (iso: string) => {
  const elapsed = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(elapsed)) return ''
  if (elapsed < HOUR) return `${Math.max(1, Math.floor(elapsed / MINUTE))}m ago`
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`
  if (elapsed < 7 * DAY) return `${Math.floor(elapsed / DAY)}d ago`
  return formatDate(iso)
}

type Addressed = { address: string; city: string; state?: string; postcode?: string; country?: string }

/** "7 Carmine Street, New York, NY 10014, United States", skipping whatever is missing. */
export const fullAddress = (place: Addressed) =>
  [place.address, place.city, [place.state, place.postcode].filter(Boolean).join(' '), place.country]
    .filter(Boolean)
    .join(', ')

const VISIBILITY_OPTIONS: { value: ListVisibility; label: string; note: string }[] = [
  { value: 'public', label: 'Public', note: 'On your profile and in feeds' },
  { value: 'unlisted', label: 'Unlisted', note: 'Only people with the link' },
  { value: 'private', label: 'Private', note: 'Only you' },
]

/** "Unlisted" or "Private"; public lists need no label. */
export const visibilityLabel = (visibility: ListVisibility) =>
  visibility === 'public' ? '' : VISIBILITY_OPTIONS.find((option) => option.value === visibility)!.label

/** Three radio rows for a list's visibility, each explaining who can see it. */
export const VisibilityPicker: m.Component<{ value: ListVisibility; onchange: (value: ListVisibility) => void }> = {
  view: ({ attrs }) =>
    m(
      'fieldset.visibility-picker',
      [m('legend', 'Who can see it')].concat(
        VISIBILITY_OPTIONS.map((option) =>
          m('label.checkbox', [
            m('input', {
              type: 'radio',
              name: 'visibility',
              value: option.value,
              checked: attrs.value === option.value,
              onchange: () => attrs.onchange(option.value),
            }),
            m('span', [m('strong', option.label), m('span.muted', option.note)]),
          ]),
        ),
      ),
    ),
}
